import React, { useState } from 'react';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { QuestionText } from 'utils/questionMarkdown';
import type { QuestionOption, ResponsePayload } from 'types';
import { questionGeneration } from 'services/api/questionGenerationClient';

interface Props {
  flowName: string;
  flowRunId: number;
  flowItemId: number;
  generationId?: number;
  questionText?: string;
  draft?: ResponsePayload | null;
  reply?: string;
  options?: QuestionOption[];
  onStarted: (runId: number) => void;
}

export const QuestionGenerationRecovery: React.FC<Props> = ({ flowName, flowRunId, flowItemId, generationId, questionText, draft, reply, options = [], onStarted }) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const data = await questionGeneration.newAttempt(flowName, flowItemId, flowRunId, generationId);
      onStarted(data.flow_run_id);
    } catch {
      setError('A new attempt could not be started. Your earlier answers are still here. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return <div data-flow-surface="card" data-testid={`question-generation-recovery-${flowItemId}`} className="w-full max-w-xl space-y-4 rounded-3xl border border-border/40 bg-white/80 p-6 sm:p-8">
    <h2 className="text-xl font-semibold">This question has changed</h2>
    <p className="text-sm text-muted-foreground">Start a new module attempt to use the updated question. Your saved answers will remain in your history. You can review or copy your current answer below before starting.</p>
    {draft && <details className="rounded-2xl border border-border/40 p-4" open>
      <summary className="cursor-pointer font-medium">Your current answer</summary>
      <div className="space-y-3 pt-3 text-sm">
        {questionText && <QuestionText text={questionText} />}
        {draft.text_value && <p className="whitespace-pre-wrap break-words">{draft.text_value}</p>}
        {draft.text_list && <ol className="list-decimal space-y-1 pl-5">{draft.text_list.map((text, index) => <li key={index} className="whitespace-pre-wrap break-words">{text}</li>)}</ol>}
        {!draft.text_value && draft.selected_options?.length ? <ol className="list-decimal space-y-1 pl-5">{draft.selected_options.map((id) => <li key={id}>{options.find((option) => option.id === id)?.front_text ?? `Saved selection ${id}`}</li>)}</ol> : null}
        {typeof draft.rating_value === 'number' && <p>Rating: {draft.rating_value}</p>}
        {typeof draft.boolean_value === 'boolean' && <p>{draft.boolean_value ? 'Yes' : 'No'}</p>}
      </div>
    </details>}
    {reply && <div className="space-y-2 text-sm"><p className="font-medium">Your unsent follow-up</p><p className="whitespace-pre-wrap break-words">{reply}</p></div>}
    <Button variant="secondary" onClick={() => setConfirming(true)}>Start new attempt</Button>
    <Dialog open={confirming} onOpenChange={(open) => { if (!busy) setConfirming(open); }}>
      <DialogContent className="max-w-md">
        <DialogTitle>Restart this module?</DialogTitle>
        <DialogDescription>Your earlier saved answers will remain in your history. The new attempt starts from the first question. Your current draft will not be copied into the new attempt.</DialogDescription>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="ghost" disabled={busy} onClick={() => setConfirming(false)}>Keep reviewing</Button>
          <Button variant="secondary" disabled={busy} onClick={() => { void start(); }}>{busy ? 'Starting…' : 'Restart module'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  </div>;
};
